import React, { useState, useEffect } from 'react';
import { Registration, EventSettings } from '../types';
import { dbService } from '../firebaseConfig';
import { generateReceiptImage } from '../utils/receiptImage';

interface SuccessPageProps {
  onBackHome: () => void;
  registration: Registration | null;
}

const SuccessPage: React.FC<SuccessPageProps> = ({ onBackHome, registration }) => {
  const [settings, setSettings] = useState<EventSettings | null>(null);
  const [sharing, setSharing] = useState(false);
  const [shareHint, setShareHint] = useState<string | null>(null);

  useEffect(() => {
    dbService.getSettings().then(setSettings).catch(console.error);
  }, []);

  const handlePrint = () => {
    window.print();
  };

  const eventLabel = settings ? `${settings.eventName} ${settings.eventEdition}` : 'Congresso de Homens IPCC 2026';

  const buildMessage = () =>
    `Olá! Segue meu comprovante de inscrição para o ${eventLabel}.\n\n` +
    `*Nome:* ${registration?.fullName}\n` +
    `*ID Inscrição:* ${registration?.id?.slice(-8).toUpperCase()}\n` +
    `*CPF:* ${registration?.cpf}`;

  const openWhatsApp = () => {
    if (!settings?.contactWhatsApp) return;
    window.open(`https://wa.me/${settings.contactWhatsApp}?text=${encodeURIComponent(buildMessage())}`, '_blank');
  };

  /**
   * Compartilha o comprovante como IMAGEM anexada.
   * - Celular (Android/iOS): abre o menu nativo de compartilhar já com a imagem → escolher WhatsApp.
   * - Computador: baixa a imagem e abre o WhatsApp com o texto, para anexar manualmente.
   */
  const handleShareReceipt = async () => {
    if (!settings || !registration) return;
    setSharing(true);
    setShareHint(null);
    try {
      const blob = await generateReceiptImage(registration, settings);
      const fileName = `comprovante-${registration.id?.slice(-8).toUpperCase() || 'inscricao'}.png`;
      const file = new File([blob], fileName, { type: 'image/png' });
      const nav: any = navigator;

      if (nav.share && nav.canShare && nav.canShare({ files: [file] })) {
        await nav.share({ files: [file], title: 'Comprovante de Inscrição', text: buildMessage() });
        return;
      }

      // Fallback: baixar imagem + abrir WhatsApp com o texto
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      setShareHint('A imagem do comprovante foi baixada. Anexe-a na conversa do WhatsApp que acabou de abrir.');
      openWhatsApp();
    } catch (err: any) {
      if (err?.name !== 'AbortError') {
        console.error(err);
        setShareHint('Não foi possível gerar a imagem. Enviando apenas o texto.');
        openWhatsApp();
      }
    } finally {
      setSharing(false);
    }
  };

  return (
    <div className="min-h-screen bg-sand-soft flex flex-col items-center justify-center px-4 py-12">
      <div className="max-w-xl w-full bg-white rounded-[40px] shadow-2xl overflow-hidden animate-in zoom-in print:shadow-none print:border print:rounded-none">
        <div className="bg-brand-dark p-8 text-white text-center print:bg-white print:text-black print:border-b relative overflow-hidden">
          <div className="absolute inset-0 texture-lines print:hidden"></div>
          <div className="relative">
            <div className="w-12 h-12 bg-brand-amber rounded-xl flex items-center justify-center mx-auto mb-4 text-2xl print:hidden">{registration?.status === 'paid' ? '🔥' : '📨'}</div>
            <h2 className="font-display text-3xl font-semibold uppercase">{registration?.status === 'paid' ? 'Comprovante de Inscrição' : 'Inscrição Recebida'}</h2>
            <p className="text-brand-amber mt-1 print:text-gray-500 uppercase tracking-widest font-black text-[10px]">{eventLabel}</p>
          </div>
        </div>

        <div className="p-10 space-y-8">
          <div className="text-center">
            <p className="text-brand-stone text-xs uppercase font-black tracking-widest mb-1">Inscrito</p>
            <h3 className="font-display text-3xl font-semibold uppercase text-brand-dark">{registration?.fullName || 'Visitante'}</h3>
          </div>

          <div className="grid grid-cols-2 gap-8 py-8 border-y border-dashed border-brand-sand">
            <div>
              <p className="text-[10px] font-black text-brand-stone uppercase tracking-widest mb-1">Inscrição #</p>
              <p className="font-mono font-bold text-brand-ember">{registration?.id?.slice(-8).toUpperCase() || '---'}</p>
            </div>
            <div>
              <p className="text-[10px] font-black text-brand-stone uppercase tracking-widest mb-1">Status</p>
              {registration?.status === 'paid' ? (
                <p className="font-bold text-green-600">CONFIRMADA</p>
              ) : registration?.receiptUrl ? (
                <p className="font-bold text-brand-ember text-xs leading-tight">COMPROVANTE ENVIADO<br /><span className="font-medium text-brand-stone">aguardando conferência</span></p>
              ) : registration?.receiptViaWhatsApp ? (
                <p className="font-bold text-brand-ember text-xs leading-tight">ENVIAR COMPROVANTE<br /><span className="font-medium text-brand-stone">pelo WhatsApp</span></p>
              ) : (
                <p className="font-bold text-brand-ember">AGUARDANDO PAGAMENTO</p>
              )}
            </div>
            <div>
              <p className="text-[10px] font-black text-brand-stone uppercase tracking-widest mb-1">Data do Evento</p>
              <p className="font-bold text-gray-700 text-sm">{settings ? `${settings.eventDate} de ${settings.eventYear}` : '16 a 18 de Outubro de 2026'}</p>
            </div>
            <div>
              <p className="text-[10px] font-black text-brand-stone uppercase tracking-widest mb-1">Chegada</p>
              <p className="font-bold text-gray-700 text-xs">{settings?.eventTime || 'Sexta-feira às 18h45'}</p>
            </div>
          </div>

          {registration?.status !== 'paid' && registration?.receiptUrl && (
            <div className="bg-green-50 border border-green-100 p-5 rounded-3xl flex items-start gap-3 print:hidden">
              <span className="text-xl">✅</span>
              <p className="text-green-800 text-xs leading-relaxed">
                <b>Recebemos seu comprovante</b> ({registration.receiptName}). A organização vai conferir o pagamento e confirmar sua vaga. Guarde este número de inscrição.
              </p>
            </div>
          )}

          {registration?.status !== 'paid' && !registration?.receiptUrl && registration?.receiptViaWhatsApp && settings?.contactWhatsApp && (
            <div className="bg-amber-50 border border-amber-200 p-5 rounded-3xl print:hidden space-y-4">
              <div className="flex items-start gap-3">
                <span className="text-xl">📱</span>
                <p className="text-amber-900 text-xs leading-relaxed">
                  <b>Falta só o comprovante.</b> Toque no botão abaixo: a conversa abre com sua identificação preenchida — é só anexar o print ou PDF do pagamento na sequência.
                </p>
              </div>
              <a
                href={`https://wa.me/${settings.contactWhatsApp}?text=${encodeURIComponent(
                  `Olá! Segue o comprovante do PIX da minha inscrição no ${eventLabel}.\n\n*Nome:* ${registration.fullName}\n*ID Inscrição:* ${registration.id?.slice(-8).toUpperCase()}\n*CPF:* ${registration.cpf}\n\n(comprovante em anexo)`
                )}`}
                target="_blank"
                rel="noreferrer"
                className="w-full bg-brand-green text-white py-4 rounded-2xl font-black uppercase tracking-widest text-xs shadow-lg flex items-center justify-center gap-2 hover:opacity-90 transition-opacity"
              >
                <span>📱</span> Enviar comprovante pelo WhatsApp
              </a>
            </div>
          )}

          <div className="bg-brand-sand/60 p-6 rounded-3xl space-y-4 print:bg-white print:border">
            <div className="flex items-start gap-3">
              <span className="text-xl">🏕️</span>
              <div>
                <p className="font-bold text-brand-dark text-sm">Chegada (sexta) e hospedagem</p>
                <p className="text-brand-stone text-xs">{settings?.retreatName || 'Chácara Renovare'}</p>
                <p className="text-brand-stone text-[10px]">{settings?.retreatAddress}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-xl">⛪</span>
              <div>
                <p className="font-bold text-brand-dark text-sm">Culto de encerramento (domingo)</p>
                <p className="text-brand-stone text-xs">{settings?.locationName || 'Igreja Presbiteriana Central de Cascavel'}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-xl">🎒</span>
              <div>
                <p className="font-bold text-brand-dark text-sm">O que levar</p>
                <p className="text-brand-stone text-[10px] leading-relaxed mt-1">
                  {(settings?.checklist || ['Bíblia', 'Roupa de cama', 'Toalha', 'Itens de higiene']).join(' • ')}
                </p>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-3 print:hidden">
            {settings?.contactWhatsApp && (
              <button
                onClick={handleShareReceipt}
                disabled={sharing}
                className="w-full bg-brand-green text-white py-5 rounded-2xl font-bold hover:opacity-90 transition-all shadow-xl flex items-center justify-center gap-3 uppercase tracking-widest text-xs disabled:opacity-60"
              >
                <span>📱</span> {sharing ? 'Gerando imagem...' : 'Compartilhar no WhatsApp'}
              </button>
            )}
            {shareHint && (
              <p className="text-center text-[11px] text-brand-ember font-medium leading-relaxed px-2">{shareHint}</p>
            )}

            <button
              onClick={handlePrint}
              className="w-full bg-brand-amber text-brand-night py-5 rounded-2xl font-bold hover:bg-brand-dark hover:text-white transition-all shadow-xl shadow-brand-amber/20 flex items-center justify-center gap-2"
            >
              <span>🖨️</span> Imprimir ou Salvar PDF
            </button>

            <button
              onClick={onBackHome}
              className="w-full text-brand-stone text-sm font-bold hover:text-brand-dark transition-colors py-2"
            >
              Voltar ao Início
            </button>
          </div>

          <p className="text-center text-[10px] text-gray-300 font-medium hidden print:block">
            Este é um documento digital. Apresente este comprovante no check-in do evento.
          </p>
        </div>
      </div>

      <p className="mt-8 text-brand-stone text-sm italic print:hidden">Tire um print ou salve este comprovante.</p>
    </div>
  );
};

export default SuccessPage;
